import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';
import PrintableNewPriceList from '../printable/PrintableNewPriceList'; // Will be created next
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';

// Interface for Structure data, ensure it includes all necessary fields for the cards
interface Structure {
  id: number;
  code: string;
  type: string;
  name: string;
  address: string;
  size: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  imageUrl?: string; // Essential for large photo
  // images?: string[]; // Removed as per user feedback
  description?: string; // Detailed information
  latitude?: number | null; // For map, can be null if not parsable
  longitude?: number | null; // For map, can be null if not parsable
  // Add any other fields that might be part of "detailed information"
}

const NewPriceListPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSettings();

  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const structureIdsParam = searchParams.get('ids');

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => {
      document.body.classList.remove('print-mode');
    };
  }, []);

  useEffect(() => {
    if (!structureIdsParam) {
      setError('Не выбраны конструкции для печати. Пожалуйста, вернитесь и выберите конструкции.');
      setLoading(false);
      setStructures([]);
      return;
    }

    const selectedIds = structureIdsParam.split(',').map(Number).filter(id => !isNaN(id) && id > 0);

    if (selectedIds.length === 0) {
      setError('Не выбраны корректные ID конструкций для печати.');
      setLoading(false);
      setStructures([]);
      return;
    }

    setLoading(true);
    setError(null);

    // Fetch details for each selected structure.
    // This might be inefficient if there are many structures.
    // A batch endpoint /api/structures/batch?ids=1,2,3 would be better.
    // For now, fetching one by one or modifying an existing endpoint.
    // Let's assume /structures/active can be filtered by ids or we fetch all and filter.
    // For simplicity, let's fetch all active and then filter.
    // In a real scenario, an endpoint like /api/structures?ids=1,2,3 would be preferred.

    axiosInstance.get<Structure[]>('/structures/active-detailed') // Assuming a new or modified endpoint that returns more details
      .then(response => {
        const allActiveStructures = response.data;
        const filteredStructures = allActiveStructures.filter(structure =>
          selectedIds.includes(structure.id)
        );
        
        if (filteredStructures.length === 0) {
          setError('Выбранные конструкции не найдены или неактивны.');
          setStructures([]);
        } else {
          // TODO: Fetch full details if /active-detailed doesn't provide everything (e.g., multiple images, full description)
          // For now, assume /active-detailed is sufficient.
          setStructures(filteredStructures);
        }
      })
      .catch(err => {
        console.error('Error fetching structures for print:', err);
        // Check if the error is due to the endpoint not existing
        if (err.response && err.response.status === 404) {
            setError('Конечная точка /structures/active-detailed не найдена. Пожалуйста, убедитесь, что она создана на бэкенде.');
        } else {
            setError('Ошибка при загрузке данных для печати.');
        }
        setStructures([]);
      })
      .finally(() => {
        setLoading(false);
        // Automatically trigger print dialog after data is loaded and rendered
        // setTimeout(() => {
        //   if (structures.length > 0 && !error) window.print();
        // }, 500); // Delay to allow rendering
      });

  }, [structureIdsParam]);

  const handlePrint = () => {
    window.print();
  };

  const companyName = settings?.companyName || settings?.name || 'Ваша Компания';
  const companyDetails = settings?.address
    ? `${settings.address}${settings.phone ? `, тел: ${settings.phone}` : ''}`
    : 'Контактная информация';
  
  const currentDate = new Date().toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
        <p className="ml-2">Загрузка данных для печати...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 text-lg mb-4">{error}</p>
        <button
          onClick={() => navigate('/structures/new-price-list')}
          className="btn-secondary inline-flex items-center"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к выбору конструкций
        </button>
      </div>
    );
  }
  
  if (structures.length === 0 && !loading) {
    return (
         <div className="p-4 text-center">
            <p className="text-gray-700 text-lg mb-4">Нет конструкций для отображения в прайс-листе.</p>
            <button
              onClick={() => navigate('/structures/new-price-list')}
              className="btn-secondary inline-flex items-center"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Вернуться к выбору конструкций
            </button>
        </div>
    );
  }

  return (
    <div className="printable-container-wrapper bg-gray-100 min-h-screen">
      <div className="p-4 bg-white shadow-md mb-4 flex justify-between items-center print:hidden">
        <div>
            <h2 className="text-xl font-semibold">Печать нового прайс-листа</h2>
            <p className="text-sm text-gray-600">Прайс-лист на {currentDate}</p>
        </div>
        <div>
          <button
            onClick={() => navigate(`/structures/new-price-list?${searchParams.toString()}`)}
            className="btn-secondary mr-2"
            title="Вернуться к выбору конструкций"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1 inline-block" />
            Назад к выбору
          </button>
          <button
            onClick={handlePrint}
            className="btn-primary"
            disabled={structures.length === 0 || loading}
          >
            <PrinterIcon className="h-5 w-5 mr-1 inline-block" />
            Распечатать
          </button>
        </div>
      </div>
      <PrintableNewPriceList
        structures={structures}
        companyName={companyName}
        companyDetails={companyDetails}
        currentDate={currentDate}
      />
    </div>
  );
};

export default NewPriceListPrintPage;